 <template>
  <div class="fixed inset-0 z-50 overflow-auto flex bg-transparent sm:bg-black sm:bg-opacity-60 sm:blur-md"> 
    <div class="fixed shadow-lg max-w-xl md:relative bottom-0 inset-x-0 align-top m-auto mt-14 bg-gray-100 sm:bg-white w-full md:h-auto top-0 overflow-auto">
      <div @click.prevent="cancel" class="flex items-center justify-between bg-white shadow px-6 py-4 text-blue-600 text-lg sm:hidden">
          <svg width="22" height="18" viewBox="0 0 22 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M21.5 7.83313H4.48297L10.6581 1.65797L9.00847 0.00830078L0.0169678 8.9998L9.00847 17.9913L10.6581 16.3416L4.48297 10.1665H21.5V7.83313Z" fill="#3182CE"/>
          </svg>
        <div class="flex">
          <svg
                class="block h-6 w-6 fill-current mr-2"
                viewBox="0 0 24 24"
                width="24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
                />
                <path d="M0 0h24v24H0z" fill="none" />
              </svg>
              <span class="block whitespace-nowrap">My Profile</span>
        </div>
        <div></div>
      </div>
      <div class="flex justify-between ml-6 mt-4">
        <h2 class="font-medium text-xl md:text-base">{{ isEdit ? 'Edit Education Background' : 'Add Education Background' }}</h2>
      </div>
      <FocusTrap>
        <form class="bg-gray-100 sm:bg-white sm:shadow rounded" @submit.prevent="submit">
          <div class="p-6">
            <div class="mb-5">
              <AgInput v-validate="'required'" class="mb-3 w-full" label="Degree Title" name="degree_title" v-model="model.degree_title" :has-error="errors.has('degree_title')">
                <span class="text-sm text-red-600">
                  {{ errors.first('degree_title') }}
                </span>
              </AgInput>
              <div class="w-full mb-3">
                <label class="block tracking-wide text-sm font-bold mb-2" :class="[errors.has('degree_level_id') ? 'text-red-700' : 'text-gray-600']">Degree Level</label>
                <div class="relative">
                  <select
                    v-validate="'required'"
                    name="degree_level_id"
                    v-model="model.degree_level_id"
                    :class="[errors.has('degree_level_id') ? 'text-red-700' : 'text-gray-600']"
                    class="
                      block
                      appearance-none
                      w-full
                      mb-1
                      leading-normal
                      border border-transparent
                      text-gray-800
                      bg-white
                      focus:outline-none
                      border border-gray-300
                      rounded
                      py-2
                      px-4
                    "
                  >
                    <option v-for="degreeLevel in degreeLevels" :key="degreeLevel.id" :value="degreeLevel.id">{{ degreeLevel.name }}</option>
                  </select>
                  <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-grey-darker">
                    <svg class="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"></path>
                    </svg>
                  </div>
                </div>
                <span class="text-sm text-red-600">
                  {{ errors.first('degree_level_id') }}
                </span>
              </div>
              <AgInput v-validate="'required'" class="mb-3 w-full" label="Institution" name="institution" v-model="model.institution" :has-error="errors.has('institution')">
                <span class="text-sm text-red-600">
                  {{ errors.first('institution') }}
                </span>
              </AgInput>
              <div class="w-full mb-3">
                <label class="block tracking-wide text-sm font-bold mb-2" :class="[errors.has('course_start') ? 'text-red-700' : 'text-gray-600']">Course Start</label>
                <flat-pickr
                  ref="courseStart"
                  v-validate="courseStartRules"
                  v-model="model.course_start"
                  :config="flatPickrConfig"
                  name="course_start"
                  class="
                    transition
                    appearance-none
                    block
                    align-middle
                    w-full
                    mb-1
                    border border-transparent
                    text-gray-800
                    focus:outline-none
                    border border-gray-300
                    rounded
                    py-2
                    px-4
                    focus:bg-white focus:border-blue-500
                  "
                  :class="[errors.has('course_start') ? 'text-red-700' : 'text-gray-600']"
                ></flat-pickr>
                <p class="text-sm text-red-700 mt-1" v-if="errors.first('course_start')">
                  {{ errors.first('course_start') }}
                </p>
              </div>
              <div class="w-full mb-3">
                <label class="block tracking-wide text-sm font-bold mb-2" :class="[errors.has('course_end') ? 'text-red-700' : 'text-gray-600']">Course End</label>
                <flat-pickr
                  v-validate="courseEndRules"
                  :allowClearing="false"
                  v-model="model.course_end"
                  :config="flatPickrConfig"
                  name="course_end"
                  class="
                    transition
                    appearance-none
                    block
                    align-middle
                    w-full
                    mb-1
                    border border-transparent
                    text-gray-800
                    focus:outline-none
                    border border-gray-300
                    rounded
                    py-2
                    px-4
                    focus:bg-white focus:border-blue-500
                  "
                  :class="[errors.has('course_end') ? 'text-red-700' : 'text-gray-600']"
                ></flat-pickr>
                <p class="text-sm text-red-700 mt-1" v-if="errors.first('course_end')">
                  {{ errors.first('course_end') && 'The selected date is not valid.' }}
                </p>
              </div>
              <div class="w-full mb-3">
                <label class="block tracking-wide text-sm font-bold mb-2" :class="[errors.has('academic_score_type') ? 'text-red-700' : 'text-gray-600']"
                  >Academic Score Type</label
                >
                <div>
                  <label>
                    <input type="radio" name="academic_score_type" v-model="model.academic_score_type" value="percentage" v-validate />
                    &nbsp;Percentage
                  </label>
                </div>
                <div>
                  <label>
                    <input type="radio" name="academic_score_type" v-model="model.academic_score_type" value="GPA" />
                    &nbsp;GPA
                  </label>
                </div>
                <span class="text-sm text-red-700">
                  {{ errors.first('academic_score_type') }}
                </span>
              </div>
              <AgInput
                v-validate=""
                class="mb-3 w-full"
                label="Academic Score"
                name="academic_score"
                v-model="model.academic_score"
                :has-error="errors.has('academic_score')"
                :min="scoreLimit.min"
                :max="scoreLimit.max"
                type="number"
              >
                <span class="text-sm text-red-700">
                  {{ errors.first('academic_score') }}
                </span>
              </AgInput>
            </div>
          </div>
          <div class="mt-2 flex flex-col md:flex-row gap-4 bg-gray-100 px-8 py-4 items-center border-t">
            <button
              :disabled="!isFormDirty"
              @click.prevent="submit"
              class="bg-blue-500 w-full md:w-auto hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
            >
              {{ isEdit ? 'Save Changes' : 'Save' }}
            </button>
            <button @click.prevent="cancel" class="w-full md:w-auto bg-slate-400 text-white font-medium py-2 px-4 rounded focus:outline-none focus:shadow-outline content-end">Cancel</button>
          </div>
        </form>
      </FocusTrap>
    </div>
  </div>
</template>
<script>
import FocusTrap from 'vue-focus-lock';
import AgInput from '@/components/Input/Input.vue';
import flatPickr from 'vue-flatpickr-component';
import monthSelectPlugin from 'flatpickr/dist/plugins/monthSelect';
import 'flatpickr/dist/plugins/monthSelect/style.css';
import 'flatpickr/dist/flatpickr.css';
import { addEducationDetails, fetchDegreeLevels, updateEducationDetails } from '../../api/calls';

export default {
  name: 'AddEducation',
  components: {
    FocusTrap,
    AgInput,
    flatPickr
  },
  props: {
    selectedData: {
      type: [Object, null],
      default: null
    }
  },
  computed: {
    isFormDirty() {
      return Object.keys(this.fields).some(key => this.fields[key].dirty) || Object.keys(this.fields).some(key => this.fields[key].changed);
    },
    isEdit() {
      return !!this.selectedData;
    },
    modifiedFieldsList() {
      return this.fields;
    },
    courseStartRules() {
      return this.model.course_end ? 'date_format:yyyy/MM|required' : 'date_format:yyyy/MM';
    },
    courseEndRules() {
      return this.model.course_start ? 'date_format:yyyy/MM|after:courseStart,true|required' : 'date_format:yyyy/MM|after:courseStart,true';
    }
  },
  watch: {
    'model.academic_score_type'(value) {
      switch (value) {
        case 'GPA':
          this.scoreLimit = { min: 0, max: 4 };
          break;

        default:
          this.scoreLimit = { min: 0, max: 100 };
          break;
      }
    }
  },
  data() {
    return {
      flatPickrConfig: {
        allowClearing: false,
        plugins: [
          new monthSelectPlugin({
            shorthand: true,
            dateFormat: 'Y/m'
          })
        ]
      },
      degreeLevels: [],
      model: {},
      scoreLimit: {
        min: 0,
        max: 0
      },
      default: null
    };
  },
  async beforeCreate() {
    try {
      const response = await fetchDegreeLevels();
      this.degreeLevels = response.data.data;
    } catch (error) {
      console.error('error', error);
    }
  },
  created() {
    if (this.selectedData) {
      // eslint-disable-next-line no-unused-vars
      const { course_start, course_end, degree_title, degree_level_id, institution, academic_score, academic_score_type, default: defaultVal, need_approval } = this.selectedData;
      this.model = {
        degree_title,
        degree_level_id,
        institution,
        academic_score,
        academic_score_type,
        course_start,
        course_end
      };
      this.default = {
        ...defaultVal
      };
    }
  },
  methods: {
    submit() {
      this.$validator.validateAll().then(async result => {
        if (result) {
          try {
            this.$loading(true);
            const { course_start, course_end, degree_title, degree_level_id, institution, academic_score, academic_score_type } = this.model;
            const updateData =
              this.selectedData &&
              (this.selectedData.need_approval
                ? this.model
                : {
                    ...(this.shouldUpdate('academic_score') && !this.isSame('academic_score') && { academic_score, academic_score_type }),
                    ...(this.shouldUpdate('academic_score_type') && !this.isSame('academic_score_type') && { academic_score, academic_score_type }),
                    ...(this.shouldUpdate('course_start') && !this.isSame('course_start') && { course_start }),
                    ...(this.shouldUpdate('course_end') && !this.isSame('course_end') && { course_end }),
                    ...(this.shouldUpdate('degree_title') && !this.isSame('degree_title') && { degree_title }),
                    ...(this.shouldUpdate('degree_level_id') && !this.isSame('degree_level_id') && { degree_level_id }),
                    ...(this.shouldUpdate('institution') && !this.isSame('institution') && { institution })
                    // ...{ degree_level_id, degree_title, institution }
                  });
            this.selectedData ? await updateEducationDetails(this.selectedData.id, updateData) : await addEducationDetails(this.model);
            this.$toasted.success(this.selectedData ? 'Your education information has been changed successfully' : 'Education detail added successfully');
            this.$emit('close');
            this.$emit('success');
          } catch (error) {
            const errors = error.response.data;
            const status = error.response.status;
            if (status === 422) {
              let errorFields = Object.keys(errors);
              for (let i = 0; i < errorFields.length; i++) {
                let field = errorFields[i];

                let errorString = errors[field][0];
                this.$validator.errors.add({
                  field: field,
                  msg: errorString
                });
              }
              return;
            }
            this.$toasted.error('Oops, Something went wrong..');
          } finally {
            this.$loading(false);
          }
        }
      });
    },
    cancel() {
      this.$emit('close');
      this.model = {};
    },
    shouldUpdate(key) {
      return (this.modifiedFieldsList[key].dirty || this.modifiedFieldsList[key].changed) ? true : !!(this.selectedData.changes && this.selectedData.changes[key]);
    },
    isSame(key) {
      if (this.default) {
        if (key === 'course_start' || key === 'course_end') {
          this.default[key].actual === this.model[key];
        }
        if (this.default[key] === this.model[key]) {
          return true;
        }
      }
      return false;
    }
  }
};
</script>
